<!-- SEO Stats Dashboard -->
<ng-container>
  @if (!mixedMode()) {
  <ai-headlines class="block mb-4"></ai-headlines>
  }
  <div class="w-full relative">
    <!-- Main Card Container — page-level chrome (title + view selector +
         add-stats / customize buttons). Hidden entirely in `mixedMode` so
         that when SEO Stats is embedded inside My Dashboard, the empty
         `card-bg` wrapper doesn't leave a stray padded box with a border
         above the pinned widget. -->
    @if (!mixedMode()) {
    <div class="card-bg p-4 rounded-2xl relative z-50">
      <!-- Header Section -->
      <div
        class="flex flex-col justify-between gap-4 [@media(max-height:620px)]:gap-2 items-start mb-6 [@media(max-height:620px)]:mb-3 z-[99]"
        [ngClass]="{'lg:flex-row lg:items-center': !commonService.isPanelOpen()}">
        <h1 class="text-2xl font-medium text-[#141437] dark:text-white [@media(max-height:620px)]:text-xl"
          [ngClass]="{'w-full': commonService.isPanelOpen(), 'w-auto': !commonService.isPanelOpen()}">
          Overall Performance Details
        </h1>

        <div
          class="flex flex-wrap items-center gap-2 sm:gap-3 justify-end [@media(max-height:620px)]:gap-2 min-w-0 max-w-full"
          [ngClass]="{'w-full justify-between': commonService.isPanelOpen(), 'w-full lg:w-auto': !commonService.isPanelOpen()}">
          <app-dropdown [options]="viewOptions" [selectedValue]="view" (selectionChange)="setActiveTab($event)"
            variant="themeBase" className="w-[190px] [@media(max-height:620px)]:w-[160px]"
            triggerClass="[@media(max-height:620px)]:!min-h-[2rem] [@media(max-height:620px)]:!py-1 [@media(max-height:620px)]:!px-2.5 [@media(max-height:620px)]:!text-xs"
            menuClass="!w-auto min-w-[240px]"></app-dropdown>

          @if(view === 'analyticsView') {
          <app-normal-date-picker (dateRangeChange)="onDateRangeFilter($event)" [showComparison]="false"
            [showGroupBy]="true" [predefinedRangesDataSets]="predefinedRanges" [selectedStartRange]="selectedStartRange"
            [selectedEndRange]="selectedEndRange" [hideWeekDayUnit]="true" [hideRightSection]="true"
            [maxDate]="selectedEndRange" [selectedPresetLabel]="'Last 90 Days'"
            triggerClass="[@media(max-height:620px)]:!min-h-[2rem] [@media(max-height:620px)]:!py-0 [@media(max-height:620px)]:!px-3 [@media(max-height:620px)]:!text-xs">
          </app-normal-date-picker>
          }

          @if (isDenefits()) {
          <button (click)="openAiAssistant()"
            class="gradient-border text-[#141437] dark:text-white text-sm lg:text-xs font-medium px-5 lg:px-3 py-2.5 lg:py-3 rounded-lg transition-all duration-200 shadow-[0px_6px_12px_-6px_#AD46FF] hover:shadow-[0px_7px_12px_-5px_#AD46FF] flex items-center gap-2 disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed">
            <app-icon-v2 name="sparkles" [size]="16" />
            Ask Prism AI
          </button>
          }

          <app-button variant="default" (buttonClick)="openPanel()" [iconName]="'circlePlus'"
            className="flex items-center justify-center gap-2 shadow-[0px_6px_12px_-6px_#AD46FF] hover:shadow-[0px_4px_8px_-4px_#AD46FF] transition-all bg-[#AD46FF] hover:bg-[#AD46FF] border-none text-white whitespace-nowrap [@media(max-height:620px)]:!h-8 [@media(max-height:620px)]:!px-3 [@media(max-height:620px)]:!py-0 [@media(max-height:620px)]:!text-xs">
            <span class="font-medium hidden sm:inline">Add Stats</span>
            <span class="font-medium sm:hidden">Add</span>
          </app-button>
          <button (click)="openCustomizeLayout()"
            class="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-sm font-medium hover:border-[#AD46FF]/60 hover:text-[#AD46FF] transition-all duration-200 shadow-sm whitespace-nowrap">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect width="7" height="7" x="3" y="3" rx="1" />
              <rect width="7" height="7" x="14" y="3" rx="1" />
              <rect width="7" height="7" x="14" y="14" rx="1" />
              <rect width="7" height="7" x="3" y="14" rx="1" />
            </svg>
            Customize layout
          </button>
        </div>
      </div>

      <!-- Analytics-view sections (Stats Overview, Clicks over Time, etc.)
           are rendered below in a customizable 12-col grid driven by
           `embeddedVisibleSections()` so each card can be reordered, resized,
           hidden, and pinned via the Customize Layout popup. -->
    </div>
    }

    <!-- Customizable 12-col grid (analytics view).
         Cards are rendered in the persisted order with the persisted
         col-span; visibility, width, and order are all controlled from
         the Customize Layout popup and applied via `applyLayoutEditor()`.
         Audit Logs are also rendered as a `@case` here so they're managed
         alongside every other section by the editor. -->
    @if(view === 'analyticsView') {
    <div
      class="grid grid-cols-12 gap-4 mt-6 [@media(max-height:620px)]:mt-4 [@media(max-height:620px)]:gap-3 grid-flow-row-dense">
      @for (sec of embeddedVisibleSections(); track trackSection($index, sec)) {
      <div [ngClass]="getSectionColClass(sec)" class="group relative">

        @if (!mixedMode()) {
        <button type="button" (click)="addToCustomDashboard(sec); $event.stopPropagation()"
          [disabled]="isAddingToCustomDashboard(sec)"
          class="group/pin absolute cursor-pointer top-2 right-2 z-20 inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full border shadow-sm text-[11px] font-medium whitespace-nowrap opacity-0 translate-y-[-2px] group-hover:opacity-100 group-hover:translate-y-0 focus-visible:opacity-100 focus-visible:translate-y-0 transition-all duration-200 disabled:cursor-default"
          [ngClass]="isAddedToCustomDashboard(sec)
            ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-rose-50 hover:text-rose-700 hover:border-rose-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800 dark:hover:bg-rose-900/30 dark:hover:text-rose-300 dark:hover:border-rose-800 !opacity-100 !translate-y-0'
            : 'bg-white/95 text-[#AD46FF] border-[#AD46FF]/30 hover:bg-violet-50 hover:border-[#AD46FF]/60 hover:shadow-md dark:bg-slate-800/95 dark:border-purple-500/40 dark:hover:bg-white/5'"
          [attr.aria-label]="isAddedToCustomDashboard(sec) ? 'Remove from My Custom Dashboard' : 'Add to My Custom Dashboard'"
          [title]="isAddedToCustomDashboard(sec) ? 'Click to remove from My Custom Dashboard' : 'Add to My Custom Dashboard'">
          @if (isAddingToCustomDashboard(sec)) {
          <svg class="animate-spin" xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24"
            fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
          </svg>
          <span>{{ isAddedToCustomDashboard(sec) ? 'Removing' : 'Adding' }}&hellip;</span>
          } @else if (isAddedToCustomDashboard(sec)) {
          <svg class="shrink-0 block group-hover/pin:hidden" xmlns="http://www.w3.org/2000/svg" width="11" height="11"
            viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.8" stroke-linecap="round"
            stroke-linejoin="round">
            <path d="M20 6 9 17l-5-5" />
          </svg>
          <svg class="shrink-0 hidden group-hover/pin:block" xmlns="http://www.w3.org/2000/svg" width="11" height="11"
            viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.8" stroke-linecap="round"
            stroke-linejoin="round">
            <path d="M5 12h14" />
          </svg>
          <span
            class="inline-block max-w-0 -ml-1.5 group-hover/pin:max-w-[220px] group-hover/pin:ml-0 focus-visible:max-w-[220px] focus-visible:ml-0 overflow-hidden whitespace-nowrap transition-all duration-300 ease-out">Remove
            from Custom Dashboard</span>
          } @else {
          <svg class="shrink-0" xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24"
            fill="none" stroke="currentColor" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round">
            <path d="M5 12h14" />
            <path d="M12 5v14" />
          </svg>
          <span
            class="inline-block max-w-0 -ml-1.5 group-hover/pin:max-w-[200px] group-hover/pin:ml-0 focus-visible:max-w-[200px] focus-visible:ml-0 overflow-hidden whitespace-nowrap transition-all duration-300 ease-out">Pin
            to Custom Dashboard</span>
          }
        </button>
        }

        @switch (sec.id) {

        <!-- ▸ Stats Cards Overview -->
        @case (SectionId.STATS_OVERVIEW) {
        <div class="card-bg rounded-2xl p-4 lg:p-6 [@media(max-height:620px)]:p-3 h-full">
          <div [ngClass]="getStatsGridClass()" class="gap-4 [@media(max-height:620px)]:gap-2.5">
            @if(isLoading()){
            <div [ngClass]="getStatsLoaderClass()">
              <app-individual-toaster></app-individual-toaster>
            </div>
            } @else {
            @for(stat of cardStatsConfig(); track $index){
            <div
              class="flex justify-start flex-col items-start border border-slate-200 h-full dark:border-line p-4 [@media(max-height:620px)]:p-2.5
                  bg-transparent rounded-2xl hover:border-[#ad46ff6e] hover:scale-[0.98] shadow-[4px_4px_20px_0px_rgba(0,0,0,0.05)] transition-all duration-300 min-h-[100px] [@media(max-height:620px)]:min-h-0">
              <div class="flex w-full h-full gap-3 [@media(max-height:620px)]:gap-2">
                <button [ngClass]="[stat.bgClass, stat.shadowClass]"
                  class="p-2 w-10 h-10 [@media(max-height:620px)]:w-8 [@media(max-height:620px)]:h-8 rounded-xl flex justify-center items-center shrink-0">
                  <app-icon [name]="stat.icon" [size]="20" [class]="stat.iconClass"
                    class="[@media(max-height:620px)]:scale-75"></app-icon>
                </button>
                <div class="w-full min-w-0 flex flex-col gap-2 [@media(max-height:620px)]:gap-1">
                  <p
                    class="m-0 text-sm [@media(max-height:620px)]:text-xs font-medium text-[#7D7D93] dark:text-ink-muted truncate">
                    {{stat.label}}</p>
                  <div class="flex flex-wrap items-end gap-x-2 gap-y-1 min-w-0">
                    <p
                      class="m-0 text-xl [@media(max-height:620px)]:text-lg font-semibold text-[#141437] dark:text-white leading-none truncate max-w-full">
                      {{stat.value}}</p>
                    <span class="text-xs flex items-center gap-1 font-medium mb-[2px] whitespace-nowrap" [ngClass]="{
                        'text-[#15B268]': stat.difference > 0,
                        'text-[#F05050]': stat.difference < 0,
                        'text-slate-400': stat.difference === 0
                      }">
                      @if(stat.difference !== 0){
                      <app-icon [name]="stat.difference > 0 ? 'trendingUp' : 'trendingDown'" [size]="14"></app-icon>
                      }
                      {{ stat.difference }}%
                    </span>
                  </div>
                </div>
              </div>
            </div>
            }
            }
          </div>
        </div>
        }

        <!-- ▸ Clicks over Time Chart -->
        @case (SectionId.CLICKS_CHART) {
        <div class="card-bg rounded-2xl p-4 [@media(max-height:620px)]:p-3 h-full">
          <div class="flex items-center justify-between mb-6 [@media(max-height:620px)]:mb-3">
            <h3 class="text-xl [@media(max-height:620px)]:text-lg font-normal text-[#141437] dark:text-white">
              Clicks over Time
            </h3>
          </div>

          @if(!isLoading() && clicksChartData().length > 0) {
          <div class="w-full h-[352px] [@media(max-height:620px)]:h-[240px]">
            <app-multi-line-chart [data1]="clicksChartData()" [primaryLineColor]="'#A855F7'" [valueKey]="'value'"
              [unit]="unit()" [showYAxis]="true" [showXAxis]="true" [showXAxisGrid]="true"
              [xAxisShowOnlyFirstLast]="false" [showAllXticks]="false" [showAllXLabels]="true"
              [xAxisSmartLabelAnchors]="true" [marginProps]="{ right: 12, bottom: 28 }" [showCircleStrokes]="true"
              [showCircleStrokesWithStroke]="true" [strokeWithLineColor]="true" [showYAxisGrid]="false"
              [showYAxisPath]="true" [curveType]="'curveMonotoneX'">
            </app-multi-line-chart>
          </div>
          } @else if (!isLoading()) {
          <div
            class="flex flex-col items-center justify-center h-[352px] [@media(max-height:620px)]:h-[240px] text-[#7D7D93] dark:text-ink-muted">
            <app-icon name="inbox" [size]="48" class="mb-4 opacity-50"></app-icon>
            <p>No data available</p>
          </div>
          }

          @if(isLoading()) {
          <div class="flex items-center justify-center h-[352px] [@media(max-height:620px)]:h-[240px]">
            <app-individual-toaster></app-individual-toaster>
          </div>
          }
        </div>
        }

        <!-- ▸ Top Keyword Ranking -->
        @case (SectionId.KEYWORD_RANKING) {
        <div class="card-bg rounded-2xl p-4 lg:p-6 [@media(max-height:620px)]:p-3 h-full flex flex-col">
          <div class="flex justify-between items-start mb-6 [@media(max-height:620px)]:mb-3">
            <div>
              <h3 class="text-xl font-normal text-[#141437] dark:text-white">
                Top Keyword Ranking
              </h3>
              <p class="text-sm text-[#7D7D93] dark:text-ink-muted">
                by Organic Clicks
              </p>
            </div>
          </div>

          <div class="overflow-hidden h-[400px] [@media(max-height:620px)]:h-[300px]">
            <app-scroll-table [showTableWithBorderAndRadius]="true" [tableData]="topKeywordRankingAnalyticsData()"
              [showHeaderIcon]="false" [previousData]="[]" [headers]="topKeywordRankingAnalyticsHeaders"
              [tableKey]="'topKeywordRankingAnalytics'" [showHeading]="false">
            </app-scroll-table>
          </div>
        </div>
        }

        <!-- ▸ AI Overview Keyword -->
        @case (SectionId.AI_OVERVIEW) {
        <div class="card-bg rounded-2xl p-4 lg:p-6 [@media(max-height:620px)]:p-3 h-full flex flex-col">
          <div class="flex flex-col sm:flex-row justify-between items-start mb-6 [@media(max-height:620px)]:mb-3">
            <div>
              <h3 class="text-xl font-normal text-[#141437] dark:text-white">
                AI Overview Keyword
              </h3>
              <p class="text-sm text-[#7D7D93] dark:text-ink-muted">
                Top 10 Keywords
              </p>
            </div>
          </div>
          <div class="overflow-hidden h-[400px] [@media(max-height:620px)]:h-[300px]">
            <app-scroll-table [showTableWithBorderAndRadius]="true" [tableData]="aiOverviewAnalyticsData()"
              [showHeaderIcon]="false" [previousData]="[]" [headers]="aiOverviewAnalyticsHeaders"
              [tableKey]="'aiOverviewAnalytics'" [showHeading]="false">
            </app-scroll-table>
          </div>
        </div>
        }

        <!-- ▸ Countries Performance -->
        @case (SectionId.COUNTRY_PERFORMANCE) {
        <div class="card-bg rounded-2xl p-4 [@media(max-height:620px)]:p-3 h-full flex flex-col">
          <div class="flex flex-col sm:flex-row justify-between items-center mb-6 [@media(max-height:620px)]:mb-3">
            <div>
              <h3 class="text-xl [@media(max-height:620px)]:text-lg font-normal text-[#141437] dark:text-white">
                Performance in Top 10 Countries
              </h3>
            </div>
          </div>
          <div class="flex-1 min-h-[400px] [@media(max-height:620px)]:min-h-[300px]">
            @if(countryPerformanceStats().length > 0) {
            <app-parallel-horizontal-bar-chart [data]="parallelChartData()" [leftLabel]="'Impressions'"
              [rightLabel]="'Clicks'" [leftColor]="'#3B82F6'" [rightColor]="'#06B6D4'" [isDarkTheme]="isDarkTheme()"
              [barHeight]="isCompactView() ? 32 : 40" [minHeight]="isCompactView() ? 320 : 400">
            </app-parallel-horizontal-bar-chart>
            } @else {
            <div class="flex flex-col items-center justify-center h-full text-[#7D7D93] dark:text-ink-muted">
              <app-icon name="globe" [size]="48" class="mb-4 opacity-50"></app-icon>
              <p>No country data available</p>
            </div>
            }
          </div>
        </div>
        }

        <!-- ▸ Top Cities -->
        @case (SectionId.TOP_CITIES) {
        <div class="card-bg rounded-2xl p-4 [@media(max-height:620px)]:p-3 h-full flex flex-col">
          <div class="flex flex-col sm:flex-row justify-between items-center mb-6 [@media(max-height:620px)]:mb-3">
            <div>
              <h3 class="text-xl [@media(max-height:620px)]:text-lg font-normal text-[#141437] dark:text-white">
                Top Cities
              </h3>
              <p class="text-sm text-[#7D7D93] dark:text-ink-muted">
                by New Users
              </p>
            </div>
          </div>
          <div class="min-w-0 h-[410px] [@media(max-height:620px)]:h-[355px]">
            @if(cityChartData().length > 0) {
            <app-horizontal-bar-chart [data]="cityChartData()" [showXAxisGrid]="true" [showYAxisGrid]="false"
              [showXAxisLabel]="true" [showBarsValue]="true" [leftMargin]="cityChartLeftMargin()"
              [forceMaxVal]="cityChartMaxValue()" [minHeight]="isCompactView() ? 300 : 350"
              [barHeight]="isCompactView() ? 30 : 35" [marginTop]="isCompactView() ? 5 : 10"
              [marginBottom]="isCompactView() ? 45 : 100" [xAxisTitle]="'Traffic'">
            </app-horizontal-bar-chart>
            } @else {
            <div class="flex flex-col items-center justify-center h-full text-[#7D7D93] dark:text-ink-muted">
              <app-icon name="inbox" [size]="48" class="mb-4 opacity-50"></app-icon>
              <p>No city data available</p>
            </div>
            }
          </div>
        </div>
        }

        <!-- ▸ Reviews and Rating Count -->
        @case (SectionId.REVIEWS_RATINGS) {
        <div class="card-bg rounded-2xl p-4 lg:p-6 [@media(max-height:620px)]:p-3 h-full">
          <div class="mb-6 [@media(max-height:620px)]:mb-3">
            <div class="text-xl font-normal text-[#141437] dark:text-white">
              Top Reviews and Rating Count
            </div>
            <span class="text-sm text-[#7D7D93] dark:text-ink-muted mt-1 block">
              Till Current Month
            </span>
          </div>

          <div [ngClass]="getReviewsRatingsGridClass()">
            @if(topReviewsRatings().length > 0) {
            @for (item of topReviewsRatings().slice(0, 3); track $index) {
            <div
              class="card-bg border transparent rounded-2xl p-6 flex flex-col items-center justify-center text-center transition-all duration-300">
              <div
                class="mb-4 border-b dark:border-b-line border-b-[#EAEAEA] w-full flex items-center justify-center pb-4">
                @if (item.platform && item.platform.toLowerCase().includes('google')) {
                <img src="google-business.svg" alt="" />
                } @else if (item.platform && item.platform.toLowerCase().includes('better')) {
                <img src="better-bureau.svg" alt="" />
                } @else if (item.platform && item.platform.toLowerCase().includes('trustpilot')) {
                <img src="trustpilot.svg" alt="" />
                } @else if (item.platform && item.platform.toLowerCase().includes('app')) {
                <img src="App-Store.svg" alt="" />
                } @else if (item.platform && item.platform.toLowerCase().includes('company')) {
                <img src="Best-Company.svg" alt="" />
                } @else if (item.platform && item.platform.toLowerCase().includes('birdeye')) {
                <img src="birdeye-Reviews.svg" alt="" />
                } @else if (item.platform && item.platform.toLowerCase().includes('capterra')) {
                <img src="capterra.svg" alt="" />
                } @else if (item.platform && item.platform.toLowerCase().includes('chamber')) {
                <img src="Chamber-of-Commerce.svg" alt="" />
                } @else if (item.platform && item.platform.toLowerCase().includes('crunchbase')) {
                <img src="crunchbase.svg" alt="" />
                } @else if (item.platform && item.platform.toLowerCase().includes('g2')) {
                <img src="g2.svg" alt="" />
                } @else if (item.platform && item.platform.toLowerCase().includes('glassdoor')) {
                <img src="Glassdoor.svg" alt="" />
                } @else if (item.platform && item.platform.toLowerCase().includes('indeed')) {
                <img src="Indeed.svg" alt="" />
                } @else if (item.platform && item.platform.toLowerCase().includes('play')) {
                <img src="Play-store.svg" alt="" />
                } @else if (item.platform && item.platform.toLowerCase().includes('serchen')) {
                <img src="Serchen.svg" alt="" />
                } @else if (item.platform && item.platform.toLowerCase().includes('sitejabber')) {
                <img src="Sitejabber.svg" alt="" />
                } @else if (item.platform && item.platform.toLowerCase().includes('sourceForge')) {
                <img src="SourceForge.svg" alt="" />
                } @else if (item.platform && item.platform.toLowerCase().includes('yelp')) {
                <img src="Yelp.svg" alt="" />
                }
              </div>

              <h3 class="text-[#141437] dark:text-white text-base font-medium mb-2">
                {{ item.platform }}
              </h3>

              <div class="text-[#A1A1AA] text-sm mb-3 font-medium">
                <span class="text-[#141437] dark:text-white text-lg font-medium">{{ item.rating }}</span>/5.0
              </div>

              <div class="flex gap-1 mb-3">
                @for (star of [1, 2, 3, 4, 5]; track star) { @if (item.rating >=
                star) {
                <app-icon name="star" [size]="18" class="text-yellow-400 fill-yellow-400" />
                } @else {
                <app-icon name="star" [size]="18" class="text-yellow-400" />
                } }
              </div>

              <div class="text-[#7D7D93] dark:text-ink-muted text-xs">
                Review count
                <span class="font-medium text-[#141437] dark:text-white">{{
                  item.reviewsCount
                  }}</span>
              </div>
            </div>
            }
            } @else {
            <div [ngClass]="getReviewsRatingsEmptySpanClass()">
              <app-icon name="inbox" [size]="48" class="mb-4 opacity-50"></app-icon>
              <p>No ratings available</p>
            </div>
            }
          </div>
        </div>
        }

        <!-- ▸ Latest Reviews Integrated Section -->
        @case (SectionId.LATEST_REVIEWS) {
        <div
          class="card-bg rounded-2xl p-4 lg:p-6 [@media(max-height:620px)]:p-3 h-full border dark:border-line border-[#EAEAEA]">
          <div
            class="flex gap-4 overflow-x-auto pb-6 no-scrollbar border-b dark:border-b-line border-b-[#EAEAEA] mb-6">
            @for (item of topReviewsRatings().slice(0, 3); track item.platformId) {
            <button (click)="selectedPlatformId.set(item.platformId ?? null)"
              [ngClass]="selectedPlatformId() === item.platformId
                  ? 'bg-[#F3F4F6] border-[#EAEAEA] dark:bg-line dark:border-line shadow-sm text-[#141437] dark:text-white'
                  : 'border-transparent text-[#7D7D93] dark:text-ink-muted hover:bg-slate-50 dark:hover:bg-slate-800/50'"
              class="flex items-center gap-2 px-4 py-2 rounded-full border transition-all whitespace-nowrap group cursor-pointer">
              @if (item.platform && item.platform.toLowerCase().includes('google')) {
              <img src="google-business.svg" class="w-5 h-5" alt="" />
              } @else if (item.platform && item.platform.toLowerCase().includes('better')) {
              <img src="better-bureau.svg" class="w-5 h-5" alt="" />
              } @else if (item.platform && item.platform.toLowerCase().includes('trustpilot')) {
              <img src="trustpilot.svg" class="w-5 h-5" alt="" />
              } @else if (item.platform && item.platform.toLowerCase().includes('app')) {
              <img src="App-Store.svg" class="w-5 h-5" alt="" />
              } @else if (item.platform && item.platform.toLowerCase().includes('company')) {
              <img src="Best-Company.svg" class="w-5 h-5" alt="" />
              } @else if (item.platform && item.platform.toLowerCase().includes('birdeye')) {
              <img src="birdeye-Reviews.svg" class="w-5 h-5" alt="" />
              } @else if (item.platform && item.platform.toLowerCase().includes('capterra')) {
              <img src="capterra.svg" class="w-5 h-5" alt="" />
              } @else if (item.platform && item.platform.toLowerCase().includes('chamber')) {
              <img src="Chamber-of-Commerce.svg" class="w-5 h-5" alt="" />
              } @else if (item.platform && item.platform.toLowerCase().includes('crunchbase')) {
              <img src="crunchbase.svg" class="w-5 h-5" alt="" />
              } @else if (item.platform && item.platform.toLowerCase().includes('g2')) {
              <img src="g2.svg" class="w-5 h-5" alt="" />
              } @else if (item.platform && item.platform.toLowerCase().includes('glassdoor')) {
              <img src="Glassdoor.svg" class="w-5 h-5" alt="" />
              } @else if (item.platform && item.platform.toLowerCase().includes('indeed')) {
              <img src="Indeed.svg" class="w-5 h-5" alt="" />
              } @else if (item.platform && item.platform.toLowerCase().includes('play')) {
              <img src="Play-store.svg" class="w-5 h-5" alt="" />
              } @else if (item.platform && item.platform.toLowerCase().includes('serchen')) {
              <img src="Serchen.svg" class="w-5 h-5" alt="" />
              } @else if (item.platform && item.platform.toLowerCase().includes('sitejabber')) {
              <img src="Sitejabber.svg" class="w-5 h-5" alt="" />
              } @else if (item.platform && item.platform.toLowerCase().includes('sourceForge')) {
              <img src="SourceForge.svg" class="w-5 h-5" alt="" />
              } @else if (item.platform && item.platform.toLowerCase().includes('yelp')) {
              <img src="Yelp.svg" class="w-5 h-5" alt="" />
              }
              <span class="text-sm font-medium">{{ item.platform }}</span>
            </button>
            }
          </div>

          <div class="flex flex-col gap-4">
            @for (review of latestReviews(); track review.id) {
            <div class="bg-[#F8F9FA] dark:bg-surface-1 rounded-xl p-5 [@media(max-height:620px)]:p-3 transition-all">
              <div class="flex flex-col gap-3 [@media(max-height:620px)]:gap-1.5">
                <div class="flex items-center gap-3 [@media(max-height:620px)]:gap-2">
                  <span
                    class="text-[#141437] dark:text-white text-base font-bold [@media(max-height:620px)]:text-sm">{{
                    review.reviewerName || 'Anonymous'
                    }}</span>
                  <div class="flex gap-0.5">
                    @for (star of [1, 2, 3, 4, 5]; track star) {
                    @if (review.rating >= star) {
                    <app-icon name="star" [size]="14" class="text-yellow-400 fill-yellow-400" />
                    } @else {
                    <app-icon name="star" [size]="14" class="text-slate-300 dark:text-slate-600" />
                    }
                    }
                  </div>
                </div>

                <p
                  class="text-[#141437] dark:text-ink-soft text-sm [@media(max-height:620px)]:text-xs leading-relaxed font-normal">
                  "{{ review.reviewText }}"
                </p>
              </div>
            </div>
            } @empty {
            <div class="py-12 text-center">
              <div
                class="inline-flex items-center justify-center w-12 h-12 rounded-full bg-slate-50 dark:bg-slate-800 mb-3">
                <app-icon name="star" class="text-slate-400" [size]="24"></app-icon>
              </div>
              <p class="text-slate-500 dark:text-ink-muted font-medium">No reviews available for this platform.</p>
            </div>
            }
          </div>
        </div>
        }

        }
      </div>
      }
    </div>

    <!-- ▸ Audit Logs (standalone)
         Rendered outside the customizable 12-col grid so it is NOT part of
         the drag-drop / Customize Layout editor or the Custom Dashboard
         pin flow. Lives on the analytics view only.
         Hidden in `mixedMode` (when this component is embedded inside the
         My Dashboard / Custom Dashboard unified grid) — the audit trail is
         a page-local diagnostic surface and should not appear alongside
         the user's pinned widgets. -->
    @if (!mixedMode()) {
    <div class="card-bg rounded-2xl p-4 lg:p-6 [@media(max-height:620px)]:p-3 mt-4 [@media(max-height:620px)]:mt-3">
      <app-audit-log-panel [entityType]="auditEntityTypes" [productPid]="productPid()" [showProductFilter]="false"
        title="SEO Stats Audit Trails">
      </app-audit-log-panel>
    </div>
    }
    }

    @if(view === 'tableView') {
    <div class="mt-4 [@media(max-height:620px)]:mt-2 relative z-40">
      <div class="card-bg rounded-2xl p-4 [@media(max-height:620px)]:p-3">
        <div class="flex flex-col gap-2 [@media(max-height:620px)]:gap-1">
          <label class="text-sm font-medium text-slate-700 dark:text-slate-300">
            Filter by Date Range
          </label>
          <div class="flex flex-wrap items-center gap-3">
            <app-dropdown #dateDropdown [minYear]="2016" [maxYear]="currentYear" [maxMonth]="currentMonth"
              type="datepicker" size="md" variant="themeBase" [isApplyButtonShown]="true"
              (selectionChange)="onTableDateFilter($event)" placeholder="Select Date Range"
              class="w-full sm:w-[400px]" />
            <app-button variant="secondary" (buttonClick)="clearTableFilters()"
              className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 border border-slate-300 dark:border-slate-600 hover:border-slate-400 dark:hover:border-slate-500 rounded-xl transition-all duration-200 whitespace-nowrap h-[42px] flex items-center justify-center">
              Clear Filters
            </app-button>
          </div>
        </div>
      </div>
    </div>

    <div class="card-bg rounded-xl p-3 sm:p-4 lg:p-6 mt-4">
      <div class="text-lg sm:text-xl text-[#141437] dark:text-white font-medium mb-4">
        SEO Details
      </div>

      <div class="mt-4">
        <app-paginated-table [tableData]="tableData()" [headers]="tableHeaders" [isCheckboxShow]="false"
          [serverSide]="true" [totalItems]="totalItems()" [currentPage]="currentPage()"
          (pageChange)="onPageChange($event)" [tableActionOptions]="tableActionOptions"
          (actionSelected)="handleTableAction($event)" [tableKey]="'seoPerformanceDetails'" />
      </div>
    </div>

    <div [ngClass]="getTwoColumnGridClass()" class="gap-3 sm:gap-4 mt-4">
      <!-- Countries Table (Server-side Pagination) -->
      <div class="card-bg rounded-xl p-3 sm:p-4 h-full">
        <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-4 mb-4">
          <div class="text-lg sm:text-xl text-[#141437] dark:text-white font-medium">
            Countries
          </div>
        </div>
        <div class="mt-4">
          <app-paginated-table [tableData]="countries()" [headers]="countryTableHeaders" [isCheckboxShow]="false"
            [serverSide]="true" [totalItems]="totalCountries()" [currentPage]="countriesPage()"
            [pageSize]="itemsPerPage" (pageChange)="onCountryPageChange($event)"
            [tableActionOptions]="tableActionWithDelete" (actionSelected)="handleCountryTableAction($event)"
            [tableKey]="'countriesTable'" />
        </div>
      </div>

      <!-- Top Cities Table (Client-side Pagination) -->
      <div class="card-bg rounded-xl p-3 sm:p-4 h-full">
        <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-4 mb-4">
          <div class="text-lg sm:text-xl text-[#141437] dark:text-white font-medium">
            Cities
          </div>
          <!-- <app-button
            variant="outline"
            (click)="openLandingPageModal()"
            title="Add City"
          >
            <span class="flex items-center gap-2">
              <app-icon name="plus" [size]="16"></app-icon> Add Stats
            </span>
          </app-button> -->
        </div>
        <div class="mt-4">
          <app-paginated-table [tableData]="paginatedCities()" [headers]="cityTableHeaders" [isCheckboxShow]="false"
            [serverSide]="true" [totalItems]="totalCityUsers()" [currentPage]="citiesPage()" [pageSize]="itemsPerPage"
            (pageChange)="onCityPageChange($event)" [tableActionOptions]="tableActionWithDelete"
            (actionSelected)="handleCityTableAction($event)" [tableKey]="'citiesTable'" />
        </div>
      </div>
    </div>

    <!-- Countries & AI Overview Grid -->
    <div [ngClass]="getTwoColumnGridClass()" class="gap-4 mt-4">
      <!-- Landing Pages Table (Client-side Pagination) -->
      <div class="card-bg rounded-xl p-3 sm:p-4 lg:p-6">
        <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-4 mb-4">
          <div class="text-lg sm:text-xl text-[#141437] dark:text-white font-medium">
            Keyword Ranking
          </div>
        </div>
        <div class="mt-4">
          <app-paginated-table [tableData]="landingPages()" [headers]="landingPageHeaders" [isCheckboxShow]="false"
            [serverSide]="true" [totalItems]="totalLandingPages()" [currentPage]="landingPagesPage()"
            [pageSize]="itemsPerPage" (pageChange)="onLandingPageChange($event)"
            [tableActionOptions]="tableActionWithDelete" (actionSelected)="handleLandingPageTableAction($event)"
            [tableKey]="'landingPagesTable'" />
        </div>
      </div>

      <!-- AI Overview Table (Server-side Pagination) -->
      <div class="card-bg rounded-xl p-3 sm:p-4 lg:p-6">
        <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-4 mb-4">
          <div class="text-lg sm:text-xl text-[#141437] dark:text-white font-medium">
            AI Overview Keyword
          </div>
        </div>
        <div class="mt-4">
          <app-paginated-table [tableData]="aiOverviewData()" [headers]="aiOverviewHeaders" [isCheckboxShow]="false"
            [serverSide]="true" [totalItems]="totalAiOverview()" [currentPage]="aiOverviewPage()"
            [pageSize]="itemsPerPage" (pageChange)="onAiOverviewPageChange($event)"
            [tableActionOptions]="tableActionWithDelete" (actionSelected)="handleAiOverviewTableAction($event)"
            [tableKey]="'aiOverviewTable'" />
        </div>
      </div>
    </div>

    <!-- Reviews Table (Client-side Pagination) -->
    <div class="card-bg rounded-xl p-3 sm:p-4 lg:p-6 mt-4">
      <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-4 mb-4">
        <div class="text-lg sm:text-xl text-[#141437] dark:text-white font-medium">
          Ratings
        </div>
        <!-- <app-button
          variant="outline"
          (click)="openLandingPageModal()"
          title="Add Review"
        >
          <span class="flex items-center gap-2">
            <app-icon name="plus" [size]="16"></app-icon> Add Stats
          </span>
        </app-button> -->
      </div>
      <div class="mt-4">
        <app-paginated-table [tableData]="reviewsRatings()" [headers]="reviewHeaders" [isCheckboxShow]="false"
          [serverSide]="true" [totalItems]="totalReviews()" [currentPage]="reviewsPage()" [pageSize]="itemsPerPage"
          (pageChange)="onReviewPageChange($event)" [tableActionOptions]="tableActionWithDelete"
          (actionSelected)="handleReviewTableAction($event)" [tableKey]="'reviewsTable'" />
      </div>
    </div>

    <!-- Detailed Reviews Table -->
    <div class="card-bg rounded-xl p-3 sm:p-4 lg:p-6 mt-4">
      <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-4 mb-4">
        <div class="text-lg sm:text-xl text-[#141437] dark:text-white font-medium">
          Reviews
        </div>
      </div>
      <div class="mt-4">
        <app-paginated-table [tableData]="detailedReviews()" [headers]="detailedReviewHeaders" [isCheckboxShow]="false"
          [serverSide]="true" [totalItems]="totalDetailedReviews()" [currentPage]="detailedReviewsPage()"
          [pageSize]="itemsPerPage" (pageChange)="onDetailedReviewPageChange($event)"
          [tableActionOptions]="tableActionWithDelete" (actionSelected)="handleDetailedReviewTableAction($event)"
          [tableKey]="'detailedReviewsTable'" />
      </div>
    </div>
    }

    <!-- Error Message -->
    @if (errorMessage()) {
    <div class="mt-4">
      <div class="bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 px-4 py-3 rounded-lg text-center">
        <p>{{ errorMessage() }}</p>
      </div>
    </div>
    }
  </div>

  <!-- Audit Logs were moved into the customizable grid as a `@case`
       inside `analyticsView`, so they're managed alongside every other
       section by the Customize Layout editor (visibility / width / order). -->

  <!--  Customize Popup Layout — Mini-Dashboard Editor.
        Inline overlay that visually mirrors the actual seo-stats sections.
        Tiles support drag-drop reordering, width resizing
        (4 / 6 / 8 / 12 chips), and visibility toggling — applied to the
        live page when the user hits Apply. -->
  <!-- ═════════════════════════════════════════════════════════════════════════ -->
  @if (isLayoutEditorOpen()) {
  <!-- z-[10000] sits above the global navbar/sidebar (which use z-[999]) so the popup never gets clipped by the dashboard header. Extra top padding gives breathing room from the page top. -->
  <div appLayoutEditorOverlay
    class="fixed inset-0 z-[10010] flex items-center justify-center p-3 sm:p-6 bg-slate-900/60 backdrop-blur-sm animate-fadeIn overflow-hidden"
    (click)="closeLayoutEditor()">

    <div
      class="layout-editor-popup relative w-full max-w-[1100px] max-h-[min(88vh,calc(100vh-2rem))] flex flex-col bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden"
      (click)="$event.stopPropagation()">

      <!--  Header -->
      <div
        class="flex flex-wrap items-center justify-between gap-3 px-5 sm:px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-gradient-to-r from-[#AD46FF]/5 via-white to-[#AD46FF]/5 dark:from-purple-500/10 dark:via-slate-900 dark:to-purple-500/10">
        <div class="flex items-center gap-3 min-w-0">
          <div
            class="w-10 h-10 rounded-xl bg-gradient-to-br from-[#AD46FF] to-[#7c3aed] flex items-center justify-center shadow-lg shadow-[#AD46FF]/30 shrink-0">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none"
              stroke="white" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
              <rect width="7" height="9" x="3" y="3" rx="1" />
              <rect width="7" height="5" x="14" y="3" rx="1" />
              <rect width="7" height="9" x="14" y="12" rx="1" />
              <rect width="7" height="5" x="3" y="16" rx="1" />
            </svg>
          </div>
          <div class="min-w-0">
            <h2 class="text-lg sm:text-xl font-semibold text-[#141437] dark:text-white truncate">
              Customize Layout
            </h2>
            <p class="text-[11px] text-violet-700 dark:text-violet-300">
              <b>Drag</b> a card to reorder &middot; click the <b>width chips</b> to resize &middot; toggle the
              <b>eye</b> to hide/show &middot; the preview mirrors the real dashboard.
            </p>
          </div>
        </div>

        <div class="flex items-center gap-2.5 flex-wrap">
          <!-- Auto Adjust Width: expands the smallest card in each row of
               the preview so any leftover columns are filled. -->
          <button type="button" (click)="autoAdjustEditorWidths()" [disabled]="!canAutoAdjustEditorWidths()"
            class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all"
            [ngClass]="canAutoAdjustEditorWidths()
              ? 'bg-white dark:bg-slate-800 text-[#AD46FF] border border-[#AD46FF]/30 hover:bg-violet-50 dark:hover:bg-white/5 hover:border-[#AD46FF]/60 shadow-sm cursor-pointer'
              : 'bg-slate-50 dark:bg-slate-800 text-slate-400 dark:text-slate-500 border border-slate-200 dark:border-slate-700 cursor-not-allowed'"
            title="Expand the smallest card in each row to fill the leftover columns">
            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="15 3 21 3 21 9" />
              <polyline points="9 21 3 21 3 15" />
              <line x1="21" y1="3" x2="14" y2="10" />
              <line x1="3" y1="21" x2="10" y2="14" />
            </svg>
            Auto Adjust Width
          </button>

          <!-- Visible count chip -->
          <span
            class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-violet-50 dark:bg-violet-500/10 text-[#AD46FF] border border-[#AD46FF]/20">
            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
            {{ editorVisibleCount() }} / {{ editorItems().length }} visible
          </span>

          <!-- Close -->
          <button type="button" (click)="closeLayoutEditor()"
            class="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            title="Close">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Layout Preview Area (mini dashboard editor) -->
      <div class="flex-1 overflow-y-auto custom-scroll bg-[#FAFAFE] dark:bg-slate-950/40 p-3 sm:p-4">

        <!-- 12-col mini grid: cdkDropList enables drag-drop reordering;
             tile widths come from `getEditorColSpanClass(item.colSpan)`. -->
        <div cdkDropList cdkDropListOrientation="mixed" (cdkDropListDropped)="onEditorCardDrop($event)"
          class="grid grid-cols-12 gap-2 sm:gap-2.5 grid-flow-row-dense">

          @for (item of editorItems(); track trackEditorItem($index, item)) {
          <div cdkDrag cdkDragBoundary=".layout-editor-popup" [cdkDragData]="item" [cdkDragDisabled]="!item.visible" [ngClass]="[
              getEditorColSpanClass(item.colSpan),
              item.visible
                ? 'border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-lg hover:-translate-y-0.5 hover:border-[#AD46FF]/40 cursor-grab active:cursor-grabbing'
                : 'border-dashed border-slate-300 dark:border-slate-600 opacity-60 cursor-not-allowed'
            ]"
            class="layout-editor-tile group relative rounded-xl border bg-white dark:bg-slate-800 transition-all duration-200 overflow-hidden">

            <!-- Drop placeholder (highlight slot during drag) -->
            <div *cdkDragPlaceholder [ngClass]="getEditorColSpanClass(item.colSpan)"
              class="rounded-xl border-2 border-dashed border-[#AD46FF]/60 bg-violet-50 dark:bg-violet-500/10 min-h-[88px]">
            </div>

            <!-- Tile header: drag handle + title + visibility toggle -->
            <div class="flex items-start justify-between gap-2 px-2.5 pt-2 pb-1">
              <div class="flex items-center gap-1.5 min-w-0">
                <span class="text-slate-300 dark:text-slate-600 group-hover:text-[#AD46FF] transition-colors shrink-0"
                  title="Drag to reorder">
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="currentColor"
                    stroke="none">
                    <circle cx="9" cy="5" r="1" />
                    <circle cx="9" cy="12" r="1" />
                    <circle cx="9" cy="19" r="1" />
                    <circle cx="15" cy="5" r="1" />
                    <circle cx="15" cy="12" r="1" />
                    <circle cx="15" cy="19" r="1" />
                  </svg>
                </span>
                <span class="text-[11px] sm:text-[12px] font-semibold text-[#141437] dark:text-white truncate"
                  [title]="item.title">
                  {{ item.title }}
                </span>
              </div>

              <!-- Visibility toggle -->
              <button type="button" (click)="toggleEditorCardVisibility(item.id); $event.stopPropagation()"
                class="p-1 rounded-md transition-colors shrink-0"
                [ngClass]="item.visible
                    ? 'text-[#AD46FF] hover:bg-violet-50 dark:hover:bg-white/5'
                    : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700'"
                [title]="item.visible ? 'Hide section' : 'Show section'">
                @if (item.visible) {
                <app-icon-v2 name="eye" [size]="14"></app-icon-v2>
                } @else {
                <app-icon-v2 name="eye-off" [size]="14"></app-icon-v2>
                }
              </button>
            </div>

            <!-- Tile body: scaled-down preview placeholder + width chips.
                 Uniform `h-10` body — the chips below carry the size cue,
                 so the body just needs enough room for the abstract
                 preview bars. -->
            <div class="px-2.5 pb-2">
              <div
                class="rounded-lg bg-gradient-to-br from-violet-50 via-white to-violet-50/50 dark:from-violet-500/10 dark:via-slate-800 dark:to-violet-500/5 border border-slate-100 dark:border-slate-700/50 h-10 sm:h-10 flex items-center justify-center mb-1.5 overflow-hidden relative">
                <!-- abstract preview bars -->
                <div class="flex items-end gap-1 w-2/3 h-1/2 opacity-70">
                  <span class="flex-1 bg-[#AD46FF]/30 rounded-sm" style="height: 40%"></span>
                  <span class="flex-1 bg-[#AD46FF]/50 rounded-sm" style="height: 70%"></span>
                  <span class="flex-1 bg-[#AD46FF]/40 rounded-sm" style="height: 55%"></span>
                  <span class="flex-1 bg-[#AD46FF]/60 rounded-sm" style="height: 90%"></span>
                  <span class="flex-1 bg-[#AD46FF]/35 rounded-sm" style="height: 50%"></span>
                  <span class="flex-1 bg-[#AD46FF]/55 rounded-sm" style="height: 80%"></span>
                </div>
                <span
                  class="absolute top-0.5 right-1 text-[9px] font-semibold text-[#AD46FF]/70 uppercase tracking-wider">
                  {{ item.colSpan }}/12
                </span>
              </div>
              <!-- Width selector — minimize-2 / maximize-2 icon buttons step
                   the card through [4 → 6 → 8 → 12] widths.
                   Buttons disable themselves at the boundary. -->
              <div class="flex items-center gap-1.5">
                <span
                  class="text-[10px] font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wide mr-0.5">Width</span>

                <!-- Decrease (minimize-2) -->
                <button type="button" (click)="decreaseEditorCardColSpan(item.id); $event.stopPropagation()"
                  [disabled]="!canDecreaseEditorCardColSpan(item)" class="p-1 rounded-md border transition-all"
                  [ngClass]="canDecreaseEditorCardColSpan(item)
                    ? 'bg-white dark:bg-slate-700 text-slate-500 dark:text-slate-300 border-slate-200 dark:border-slate-600 hover:border-[#AD46FF]/50 hover:text-[#AD46FF] hover:bg-violet-50 dark:hover:bg-white/5'
                    : 'bg-slate-50 dark:bg-slate-800 text-slate-300 dark:text-slate-600 border-slate-100 dark:border-slate-700 cursor-not-allowed'"
                  title="Decrease width">
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="4 14 10 14 10 20" />
                    <polyline points="20 10 14 10 14 4" />
                    <line x1="14" y1="10" x2="21" y2="3" />
                    <line x1="3" y1="21" x2="10" y2="14" />
                  </svg>
                </button>

                <!-- Current width pill — Small / Medium / Large / Full -->
                <span
                  class="inline-flex items-center justify-center min-w-[58px] h-[20px] px-2 rounded-md text-[10px] font-semibold bg-[#AD46FF]/10 text-[#AD46FF] border border-[#AD46FF]/20 select-none whitespace-nowrap">
                  {{ getEditorColSpanLabel(item.colSpan) }}
                </span>

                <!-- Increase (maximize-2) -->
                <button type="button" (click)="increaseEditorCardColSpan(item.id); $event.stopPropagation()"
                  [disabled]="!canIncreaseEditorCardColSpan(item)" class="p-1 rounded-md border transition-all"
                  [ngClass]="canIncreaseEditorCardColSpan(item)
                    ? 'bg-white dark:bg-slate-700 text-slate-500 dark:text-slate-300 border-slate-200 dark:border-slate-600 hover:border-[#AD46FF]/50 hover:text-[#AD46FF] hover:bg-violet-50 dark:hover:bg-white/5'
                    : 'bg-slate-50 dark:bg-slate-800 text-slate-300 dark:text-slate-600 border-slate-100 dark:border-slate-700 cursor-not-allowed'"
                  title="Increase width">
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="15 3 21 3 21 9" />
                    <polyline points="9 21 3 21 3 15" />
                    <line x1="21" y1="3" x2="14" y2="10" />
                    <line x1="3" y1="21" x2="10" y2="14" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
          }
        </div>

        @if (editorItems().length === 0) {
        <div class="text-center py-16 text-slate-500 dark:text-slate-400">
          <p class="text-sm">No layout items to customize.</p>
        </div>
        }
      </div>

      <!-- ▶ Footer -->
      <div
        class="flex items-center justify-between gap-3 px-5 sm:px-6 py-3.5 border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
        <button type="button" (click)="resetLayoutEditorToDefaults()"
          class="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-[#AD46FF] hover:bg-violet-50 dark:hover:bg-white/5 transition-colors">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
            <path d="M3 3v5h5" />
          </svg>
          Set Default
        </button>

        <div class="flex items-center gap-2">
          <button type="button" (click)="closeLayoutEditor()"
            class="px-4 py-2 rounded-lg text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors">
            Cancel
          </button>
          <button type="button" (click)="applyLayoutEditor()" [disabled]="editorVisibleCount() === 0"
            class="px-5 py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-[#AD46FF] to-[#7c3aed] hover:shadow-lg hover:shadow-[#AD46FF]/30 hover:-translate-y-px disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 transition-all">
            Apply
          </button>
        </div>
      </div>
    </div>
  </div>
  }

  <!-- Add Stats Slide Panel -->
  @if (panelOpen()) {
  <app-slide-panel [open]="panelOpen()" [title]="isEditingMetric() ? 'Edit SEO Stats' : 'Add New SEO Stats'"
    (closed)="closePanel()" class="flex-1 text-[#141437] dark:text-white"
    panelClass="min-w-[600px] lg:min-w-[700px] max-w-[800px] h-full flex flex-col overflow-hidden card-bg border-l border-[#EAEAEA] dark:border-line">
    <form [formGroup]="form" (ngSubmit)="submitForm()" class="flex flex-col flex-1 overflow-hidden h-full">
      <section class="flex flex-col gap-4 flex-1 overflow-y-auto pr-1 py-2">
        <!-- Date Reference -->
        <div class="w-full flex flex-col gap-2 relative z-40 mt-2">
          <label class="text-sm font-medium text-slate-600 dark:text-ink-muted">Select Performance Period</label>
          <app-dropdown [minYear]="2016" [maxYear]="currentYear" [maxMonth]="currentMonth" type="datepicker"
            [iconName]="'calendar'" variant="themeBase" [selectedValue]="$safeNavigationMigration(form.get('dateReference')?.value)"
            formControlName="dateReference" placeholder="Select Month & Year" [fullWidth]="true"
            [closeButtonText]="'Apply'" [error]="getErrorMessage('dateReference')"></app-dropdown>
        </div>

        <!-- Tabs -->
        <div
          class="flex items-center gap-1 [@media(max-height:620px)]:gap-0.5 p-1 [@media(max-height:620px)]:p-0.5 bg-slate-100 dark:bg-line/40 rounded-xl w-full mt-4 [@media(max-height:620px)]:mt-2">
          <button type="button" (click)="!isEditingMetric() && activeStatsTab.set('seo-metrics')"
            class="px-5 [@media(max-height:620px)]:px-3 py-2 [@media(max-height:620px)]:py-1 rounded-lg text-sm font-medium transition-all duration-300 cursor-pointer relative flex items-center gap-2"
            [class.opacity-50]="isEditingMetric() && activeStatsTab() !== 'seo-metrics'"
            [class.cursor-not-allowed]="isEditingMetric() && activeStatsTab() !== 'seo-metrics'"
            [class]="activeStatsTab() === 'seo-metrics' ? 'bg-white dark:bg-purple-500 text-[#AD46FF] dark:text-white shadow-sm' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'">
            Metrics
            @if (hasTabError('seo-metrics')) {
            <app-icon name="triangleAlert" class="text-yellow-500" [size]="14"></app-icon>
            }
          </button>
          <button type="button" (click)="!isEditingMetric() && activeStatsTab.set('landing')"
            class="px-5 [@media(max-height:620px)]:px-3 py-2 [@media(max-height:620px)]:py-1 rounded-lg text-sm font-medium transition-all duration-300 cursor-pointer relative flex items-center gap-2"
            [class.opacity-50]="isEditingMetric() && activeStatsTab() !== 'landing'"
            [class.cursor-not-allowed]="isEditingMetric() && activeStatsTab() !== 'landing'"
            [class]="activeStatsTab() === 'landing' ? 'bg-white dark:bg-purple-500 text-[#AD46FF] dark:text-white shadow-sm' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'">
            Keyword Ranking
            @if (hasTabError('landing')) {
            <app-icon name="triangleAlert" class="text-yellow-500" [size]="14"></app-icon>
            }
          </button>
          <button type="button" (click)="!isEditingMetric() && activeStatsTab.set('cities')"
            class="px-5 [@media(max-height:620px)]:px-3 py-2 [@media(max-height:620px)]:py-1 rounded-lg text-sm font-medium transition-all duration-300 cursor-pointer relative flex items-center gap-2"
            [class.opacity-50]="isEditingMetric() && activeStatsTab() !== 'cities'"
            [class.cursor-not-allowed]="isEditingMetric() && activeStatsTab() !== 'cities'"
            [class]="activeStatsTab() === 'cities' ? 'bg-white dark:bg-purple-500 text-[#AD46FF] dark:text-white shadow-sm' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'">
            Regions
            @if (hasTabError('cities')) {
            <app-icon name="triangleAlert" class="text-yellow-500" [size]="14"></app-icon>
            }
          </button>
          <button type="button" (click)="!isEditingMetric() && activeStatsTab.set('rating')"
            class="px-5 [@media(max-height:620px)]:px-3 py-2 [@media(max-height:620px)]:py-1 rounded-lg text-sm font-medium transition-all duration-300 cursor-pointer relative flex items-center gap-2"
            [class.opacity-50]="isEditingMetric() && activeStatsTab() !== 'rating'"
            [class.cursor-not-allowed]="isEditingMetric() && activeStatsTab() !== 'rating'"
            [class]="activeStatsTab() === 'rating' ? 'bg-white dark:bg-purple-500 text-[#AD46FF] dark:text-white shadow-sm' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'">
            Rating
            @if (hasTabError('rating')) {
            <app-icon name="triangleAlert" class="text-yellow-500" [size]="14"></app-icon>
            }
          </button>
          <button type="button" (click)="!isEditingMetric() && activeStatsTab.set('reviews')"
            class="px-5 [@media(max-height:620px)]:px-3 py-2 [@media(max-height:620px)]:py-1 rounded-lg text-sm font-medium transition-all duration-300 cursor-pointer relative flex items-center gap-2"
            [class.opacity-50]="isEditingMetric() && activeStatsTab() !== 'reviews'"
            [class.cursor-not-allowed]="isEditingMetric() && activeStatsTab() !== 'reviews'"
            [class]="activeStatsTab() === 'reviews' ? 'bg-white dark:bg-purple-500 text-[#AD46FF] dark:text-white shadow-sm' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'">
            Reviews
            @if (hasTabError('reviews')) {
            <app-icon name="triangleAlert" class="text-yellow-500" [size]="14"></app-icon>
            }
          </button>
          <button type="button" (click)="!isEditingMetric() && activeStatsTab.set('ai-overview')"
            class="px-5 [@media(max-height:620px)]:px-3 py-2 [@media(max-height:620px)]:py-1 rounded-lg text-sm font-medium transition-all duration-300 cursor-pointer relative flex items-center gap-2"
            [class.opacity-50]="isEditingMetric() && activeStatsTab() !== 'ai-overview'"
            [class.cursor-not-allowed]="isEditingMetric() && activeStatsTab() !== 'ai-overview'"
            [class]="activeStatsTab() === 'ai-overview' ? 'bg-white dark:bg-purple-500 text-[#AD46FF] dark:text-white shadow-sm' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'">
            AI Overview
            @if (hasTabError('ai-overview')) {
            <app-icon name="triangleAlert" class="text-yellow-500" [size]="14"></app-icon>
            }
          </button>
        </div>

        <!-- SEO-Metrics Tab Content -->
        @if(activeStatsTab() === 'seo-metrics') {
        <div class="grid grid-cols-2 gap-4 [@media(max-height:620px)]:gap-2 [@media(max-height:620px)]:mt-2">
          <app-input (keydown)="preventDecimal($event)" variant="themeBase" type="number" placeholder="Enter Clicks"
            label="Clicks" formControlName="clicks"
            inputClass="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            [error]="getErrorMessage('clicks')"></app-input>

          <app-input (keydown)="preventDecimal($event)" variant="themeBase" type="number"
            placeholder="Enter Impressions" label="Impressions"
            inputClass="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            formControlName="impressions" [error]="getErrorMessage('impressions')"></app-input>

          <app-input (keydown)="preventDecimal($event)" variant="themeBase" type="number"
            placeholder="Enter Active Visitors" label="Active Visitors"
            inputClass="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            formControlName="activeVisitors" [error]="getErrorMessage('activeVisitors')"></app-input>

          <app-input (keydown)="preventDecimal($event)" variant="themeBase" type="number"
            placeholder="Enter Unique Visitors" label="Unique Visitors"
            inputClass="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            formControlName="uniqueVisitors" [error]="getErrorMessage('uniqueVisitors')"></app-input>

          <div class="col-span-2">
            <app-input (keydown)="preventDecimal($event)" variant="themeBase" type="number" placeholder="Enter Traffic"
              label="Traffic"
              inputClass="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              formControlName="traffic" [error]="getErrorMessage('traffic')"></app-input>
          </div>
        </div>
        }

        <!-- Landing Tab Content -->
        @if(activeStatsTab() === 'landing') {
        <div class="space-y-4 mt-6" formArrayName="landingRows">
          @for (row of landingRows.controls; track row; let i = $index) {
          <div [formGroupName]="i" class="flex items-start gap-4 group animate-fadeIn">

            <div class="flex-1 relative keyword-input-container">
              <app-input (keydown)="preventLeadingSpace($event)" (valueChange)="onKeywordInput($event, i, 'landing')"
                (focus)="onKeywordInput($safeNavigationMigration(row.get('landingUrl')?.value), i, 'landing')" variant="themeBase"
                placeholder="Keyword" label="Keyword" formControlName="landingUrl"
                [error]="getErrorMessage('landingRows.' + i + '.landingUrl')">
              </app-input>

              @if (showAutocomplete() && activeRowIndex() === i && activeKeywordTab() === 'landing') {
              <div
                class="absolute z-[2000] w-full mt-1 bg-white dark:bg-surface-1 border border-slate-200 dark:border-line rounded-xl shadow-[0_20px_50px_rgba(0,0,0,0.3)] max-h-60 overflow-y-auto no-scrollbar py-2 autocomplete-dropdown">
                @for (opt of filteredKeywords(); track opt.value) {
                <div (mousedown)="selectKeyword(opt.label, i, 'landing'); $event.preventDefault()"
                  class="px-4 py-2.5 text-sm cursor-pointer hover:bg-slate-100 dark:hover:bg-line text-[#141437] dark:text-ink-soft transition-all duration-200">
                  {{ opt.label }}
                </div>
                } @empty {
                <div
                  class="px-4 py-3 text-sm text-slate-400 dark:text-ink-subtle italic bg-slate-50/50 dark:bg-slate-900/20">
                  No keywords found
                </div>
                }
              </div>
              }
            </div>

            <div class="w-32">
              <app-input (keydown)="preventDecimal($event)" variant="themeBase" type="number" placeholder="Ranking"
                label="Ranking"
                inputClass="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                formControlName="landingClicks" [error]="getErrorMessage('landingRows.' + i + '.landingClicks')">
              </app-input>
            </div>

            @if (!isEditingMetric() && landingRows.length > 1) {
            <app-button type="button" variant="ghost" (buttonClick)="removeLandingRow(i)"
              className="mt-7 px-2 text-slate-400 hover:text-red-500 transition-colors">
              <app-icon name="trash" [size]="18"></app-icon>
            </app-button>
            }
          </div>
          }

          @if (!isEditingMetric()) {
          <div class="pt-2 flex justify-start">
            <app-button type="button" variant="outline" (buttonClick)="addLandingRow()"
              [disabled]="landingRows.length >= 10" size="sm"
              className="w-fit border-dashed border-2 flex items-center justify-center gap-2 text-[#7D7D93] dark:text-ink-muted hover:text-[#AD46FF] dark:hover:text-white hover:border-[#AD46FF] dark:hover:border-line-strong bg-transparent py-1.5 px-4 h-[36px]">
              <app-icon name="circlePlus" [size]="14"></app-icon>
              <span class="text-xs font-semibold uppercase tracking-wider">ADD MORE</span>
            </app-button>
          </div>
          }
        </div>
        }


        <!-- Cities Tab Content -->
        @if(activeStatsTab() === 'cities') {
        <div class="space-y-8 mt-6">
          <!-- Cities Subsection -->
          <div class="space-y-4" formArrayName="cityRows">
            <h3 class="text-sm font-semibold text-slate-700 dark:text-slate-300 px-1">Cities (Optional)</h3>
            <div
              class="p-6 border border-slate-100 dark:border-slate-800 rounded-xl bg-slate-50/50 dark:bg-slate-800/20 space-y-4">
              @for (row of cityRows.controls; track row) {
              <div [formGroupName]="$index" class="flex items-start gap-4 group animate-fadeIn relative">
                <div class="flex-1">
                  <app-input (keydown)="preventLeadingSpace($event)" variant="themeBase" placeholder="City"
                    label="City Name" formControlName="cityName"
                    [error]="getErrorMessage('cityRows.' + $index + '.cityName')">
                  </app-input>
                </div>
                <div class="w-32">
                  <app-input (keydown)="preventDecimal($event)" variant="themeBase" type="number" placeholder="Traffic"
                    label="Traffic"
                    inputClass="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    formControlName="cityUserCount"
                    [error]="getErrorMessage('cityRows.' + $index + '.cityUserCount')"></app-input>
                </div>
                @if (!isEditingMetric() && cityRows.length > 1) {
                <app-button type="button" variant="ghost" (buttonClick)="removeCityRow($index)"
                  className="mt-8 px-2 text-slate-400 hover:text-red-500 transition-colors">
                  <app-icon name="trash" [size]="18"></app-icon>
                </app-button>
                }
              </div>
              }
            </div>
            @if (!isEditingMetric()) {
            <div class="pt-2 flex justify-start">
              <app-button type="button" variant="outline" (buttonClick)="addCityRow()"
                [disabled]="cityRows.length >= 10" size="sm"
                className="w-fit border-dashed border-2 flex items-center justify-center gap-2 text-[#7D7D93] dark:text-ink-muted hover:text-[#AD46FF] dark:hover:text-white hover:border-[#AD46FF] dark:hover:border-line-strong bg-transparent py-1.5 px-4 h-[36px]">
                <app-icon name="circlePlus" [size]="14"></app-icon>
                <span class="text-xs font-semibold uppercase tracking-wider">ADD MORE</span>
              </app-button>
            </div>
            }
          </div>

          <!-- Countries Subsection -->
          <div class="space-y-4" formArrayName="countryRows">
            <h3 class="text-sm font-semibold text-slate-700 dark:text-slate-300 px-1">Countries (Optional)</h3>
            <div
              class="p-6 border border-slate-100 dark:border-slate-800 rounded-xl bg-slate-50/50 dark:bg-slate-800/20 space-y-4">
              @for (row of countryRows.controls; track row) {
              <div [formGroupName]="$index" class="flex items-start gap-4 group animate-fadeIn relative">
                <div class="flex-1">
                  <app-dropdown [options]="countryOptions()" placeholder="Select Country" title="Country"
                    formControlName="country" [error]="getErrorMessage('countryRows.' + $index + '.country')"
                    variant="themeBase" size="lg" titleClass="mb-2">
                  </app-dropdown>
                </div>
                <div class="w-32">
                  <app-input (keydown)="preventDecimal($event)" variant="themeBase" type="number"
                    placeholder="Impressions" label="Impressions"
                    inputClass="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    formControlName="countryImpressions"
                    [error]="getErrorMessage('countryRows.' + $index + '.countryImpressions')"></app-input>
                </div>
                <div class="w-32">
                  <app-input (keydown)="preventDecimal($event)" variant="themeBase" type="number" placeholder="Clicks"
                    label="Clicks"
                    inputClass="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    formControlName="countryClicks"
                    [error]="getErrorMessage('countryRows.' + $index + '.countryClicks')"></app-input>
                </div>
                @if (!isEditingMetric() && countryRows.length > 1) {
                <app-button type="button" variant="ghost" (buttonClick)="removeCountryRow($index)"
                  className="mt-8 px-2 text-slate-400 hover:text-red-500 transition-colors">
                  <app-icon name="trash" [size]="18"></app-icon>
                </app-button>
                }
              </div>
              }
            </div>
            @if (!isEditingMetric()) {
            <div class="pt-2 flex justify-start">
              <app-button type="button" variant="outline" (buttonClick)="addCountryRow()"
                [disabled]="countryRows.length >= 10" size="sm"
                className="w-fit border-dashed border-2 flex items-center justify-center gap-2 text-[#7D7D93] dark:text-ink-muted hover:text-[#AD46FF] dark:hover:text-white hover:border-[#AD46FF] dark:hover:border-line-strong bg-transparent py-1.5 px-4 h-[36px]">
                <app-icon name="circlePlus" [size]="14"></app-icon>
                <span class="text-xs font-semibold uppercase tracking-wider">ADD MORE</span>
              </app-button>
            </div>
            }
          </div>
        </div>
        }

        <!-- Rating Tab Content (Summary) -->
        @if(activeStatsTab() === 'rating') {
        <div class="space-y-4 mt-6" formArrayName="ratingRows">
          @for (row of ratingRows.controls; track row) {
          <div [formGroupName]="$index" class="flex items-start gap-4 group animate-fadeIn">
            <div class="flex-1">
              <app-dropdown [options]="platformOptions()" placeholder="Select Platform" title="Platform"
                formControlName="ratingPlatform" [error]="getErrorMessage('ratingRows.' + $index + '.ratingPlatform')"
                variant="themeBase" size="lg" titleClass="mb-2">
              </app-dropdown>
            </div>
            <div class="w-32">
              <app-input variant="themeBase" type="number" placeholder="4.5" label="Rating"
                formControlName="ratingValue"
                inputClass="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                [error]="getErrorMessage('ratingRows.' + $index + '.ratingValue')"></app-input>
            </div>
            <div class="w-32">
              <app-input (keydown)="preventDecimal($event)" variant="themeBase" type="number" placeholder="100"
                label="Count" formControlName="ratingCount"
                inputClass="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                [error]="getErrorMessage('ratingRows.' + $index + '.ratingCount')"></app-input>
            </div>
            @if (!isEditingMetric() && ratingRows.length > 1) {
            <app-button type="button" variant="ghost" (buttonClick)="removeRatingRow($index)"
              className="mt-7 px-2 text-slate-400 hover:text-red-500 transition-colors">
              <app-icon name="trash" [size]="18"></app-icon>
            </app-button>
            }
          </div>
          }
          @if (!isEditingMetric()) {
          <div class="pt-2 flex justify-start">
            <app-button type="button" variant="outline" (buttonClick)="addRatingRow()"
              [disabled]="ratingRows.length >= 10" size="sm"
              className="w-fit border-dashed border-2 flex items-center justify-center gap-2 text-[#7D7D93] dark:text-ink-muted hover:text-[#AD46FF] dark:hover:text-white hover:border-[#AD46FF] dark:hover:border-line-strong bg-transparent py-1.5 px-4 h-[36px]">
              <app-icon name="circlePlus" [size]="14"></app-icon>
              <span class="text-xs font-semibold uppercase tracking-wider">ADD MORE</span>
            </app-button>
          </div>
          }
        </div>
        }

        <!-- Reviews Tab Content (Detailed) -->
        @if(activeStatsTab() === 'reviews') {
        <div class="space-y-6 mt-6 pb-6" formArrayName="reviewRows">
          @for (row of reviewRows.controls; track row) {
          <div [formGroupName]="$index"
            class="p-4 border border-slate-200 dark:border-slate-800 rounded-xl space-y-4 relative group animate-fadeIn shadow-sm bg-slate-50/30 dark:bg-white/5">
            <div class="flex items-start gap-4">
              <div class="w-full">
                <div class="flex-1">
                  <app-dropdown [options]="platformOptions()" placeholder="Select Platform" title="Platform"
                    formControlName="reviewPlatform"
                    [error]="getErrorMessage('reviewRows.' + $index + '.reviewPlatform')" variant="themeBase" size="lg">
                  </app-dropdown>
                </div>
              </div>
              <div class="w-full flex">
                <div class="flex-1 mr-3">
                  <app-input (keydown)="preventLeadingSpace($event)" variant="themeBase" placeholder="YYYY-MM-DD"
                    label="Review Date" formControlName="reviewDate" size="md" mask="date"
                    [error]="getErrorMessage('reviewRows.' + $index + '.reviewDate')">
                  </app-input>
                </div>
                <div class="w-32">
                  <app-input variant="themeBase" type="number" placeholder="4.5" label="Rating"
                    formControlName="reviewRating" size="md"
                    inputClass="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    [error]="getErrorMessage('reviewRows.' + $index + '.reviewRating')"></app-input>
                </div>
              </div>
            </div>

            <div class="flex items-start gap-4">
              <div class="w-full">
                <div class="">
                  <app-input (keydown)="preventLeadingSpace($event)" variant="themeBase" placeholder="John Doe"
                    label="Reviewer Name" formControlName="reviewerName" size="md"
                    [error]="getErrorMessage('reviewRows.' + $index + '.reviewerName')"></app-input>
                </div>
              </div>
              <div class="w-full">
                <div class="">
                  <app-input (keydown)="preventLeadingSpace($event)" variant="themeBase" placeholder="john@example.com"
                    label="Reviewer Email" formControlName="reviewerEmail" size="md"
                    [error]="getErrorMessage('reviewRows.' + $index + '.reviewerEmail')"></app-input>
                </div>
              </div>
              <!-- Spacer to align fields with row 1 -->
              <!-- <div class="w-32"></div> -->
            </div>

            <div class="flex items-start gap-3">
              <div class="flex-1">
                <app-textarea (keydown)="preventLeadingSpace($event)" variant="custom"
                  placeholder="Great service! Highly recommended." label="Review Text" formControlName="reviewText"
                  [rows]="3" [error]="getErrorMessage('reviewRows.' + $index + '.reviewText')"></app-textarea>
              </div>
              @if (!isEditingMetric() && reviewRows.length > 1) {
              <div class="mt-7">
                <app-button type="button" variant="ghost" (buttonClick)="removeReviewRow($index)"
                  className="px-2 text-slate-400 hover:text-red-500 transition-colors">
                  <app-icon name="trash" [size]="18"></app-icon>
                </app-button>
              </div>
              }
            </div>
          </div>
          }
          @if (!isEditingMetric()) {
          <div class="pt-2 flex justify-start">
            <app-button type="button" variant="outline" (buttonClick)="addReviewRow()"
              [disabled]="reviewRows.length >= 10" size="sm"
              className="w-fit border-dashed border-2 flex items-center justify-center gap-2 text-[#7D7D93] dark:text-ink-muted hover:text-[#AD46FF] dark:hover:text-white hover:border-[#AD46FF] dark:hover:border-line-strong bg-transparent py-1.5 px-4 h-[36px]">
              <app-icon name="circlePlus" [size]="14"></app-icon>
              <span class="text-xs font-semibold uppercase tracking-wider">ADD MORE</span>
            </app-button>
          </div>
          }
        </div>
        }

        <!-- AI Overview Tab Content -->
        @if(activeStatsTab() === 'ai-overview') {
        <div class="space-y-6 mt-6 pb-6" formArrayName="aiOverviewRows">
          @for (row of aiOverviewRows.controls; track row; let i = $index) {
          <div [formGroupName]="i" class="flex flex-col sm:flex-row items-start gap-4 animate-fadeIn">
            <div class="w-full sm:w-auto flex items-start gap-2 flex-1 keyword-input-container">
              <div class="flex-1 relative">
                <app-input (keydown)="preventLeadingSpace($event)" variant="themeBase" placeholder="Keyword"
                  label="AI Overview Keyword" formControlName="aiKeyword"
                  (valueChange)="onKeywordInput($event, i, 'ai-overview')"
                  (focus)="onKeywordInput($safeNavigationMigration(row.get('aiKeyword')?.value), i, 'ai-overview')"
                  [error]="getErrorMessage('aiOverviewRows.' + i + '.aiKeyword')"></app-input>

                @if (showAutocomplete() && activeRowIndex() === i && activeKeywordTab() === 'ai-overview') {
                <div
                  class="absolute z-[2000] w-full mt-1 bg-white dark:bg-surface-1 border border-slate-200 dark:border-line rounded-xl shadow-[0_20px_50px_rgba(0,0,0,0.3)] max-h-60 overflow-y-auto no-scrollbar py-2 autocomplete-dropdown">
                  @for (opt of filteredKeywords(); track opt.value) {
                  <div (mousedown)="selectKeyword(opt.label, i, 'ai-overview'); $event.preventDefault()"
                    class="px-4 py-2.5 text-sm cursor-pointer hover:bg-slate-100 dark:hover:bg-line text-[#141437] dark:text-ink-soft transition-all duration-200">
                    {{ opt.label }}
                  </div>
                  } @empty {
                  <div
                    class="px-4 py-3 text-sm text-slate-400 dark:text-ink-subtle italic bg-slate-50/50 dark:bg-slate-900/20">
                    No keywords found
                  </div>
                  }
                </div>
                }
              </div>
            </div>
            <div class="flex-[2] w-full sm:w-auto flex items-start gap-2">
              <div class="flex-1">
                <app-input (keydown)="preventLeadingSpace($event)" variant="themeBase"
                  placeholder="https://example.com/page" label="Snapshot Link" formControlName="snapshotLink"
                  [error]="getErrorMessage('aiOverviewRows.' + i + '.snapshotLink')"></app-input>
              </div>
              @if (!isEditingMetric() && aiOverviewRows.length > 1) {
              <div class="mt-7">
                <app-button type="button" variant="ghost" (buttonClick)="removeAiOverviewRow(i)"
                  className="px-2 text-slate-400 hover:text-red-500 transition-colors">
                  <app-icon name="trash" [size]="18"></app-icon>
                </app-button>
              </div>
              }
            </div>
          </div>
          }
          @if (!isEditingMetric()) {
          <div class="pt-2 flex justify-start">
            <app-button type="button" variant="outline" (buttonClick)="addAiOverviewRow()"
              [disabled]="aiOverviewRows.length >= 10" size="sm"
              className="w-fit border-dashed border-2 flex items-center justify-center gap-2 text-[#7D7D93] dark:text-ink-muted hover:text-[#AD46FF] dark:hover:text-white hover:border-[#AD46FF] dark:hover:border-line-strong bg-transparent py-1.5 px-4 h-[36px]">
              <app-icon name="circlePlus" [size]="14"></app-icon>
              <span class="text-xs font-semibold uppercase tracking-wider">ADD MORE</span>
            </app-button>
          </div>
          }
        </div>
        }
      </section>

      @if(errorMessage()) {
      <app-alert [message]="errorMessage()" [duration]="5000" [type]="'error'" [closable]="true" [showProgress]="true"
        (close)="errorMessage.set('')"></app-alert>
      } @if(successMessage()) {
      <app-alert [message]="successMessage()" [duration]="3000" [type]="'success'" [closable]="true"
        [showProgress]="true" (close)="successMessage.set('')"></app-alert>
      }

      <div
        class="grid grid-cols-2 gap-4 p-5 sticky bottom-0 bg-white dark:bg-surface-1 border-t border-[#EAEAEA] dark:border-line mt-auto">
        <app-button type="button" variant="defaultBorder" (buttonClick)="closePanel()"
          className="w-full h-[45px]">Cancel</app-button>
        <app-button type="submit" variant="default" className="w-full h-[45px]"
          [disabled]="!isAtLeastOneMetricFilled()">
          Submit
        </app-button>
      </div>
    </form>
  </app-slide-panel>
  }

  <!-- Landing Page Modal -->
  @if (isLandingPageModalOpen()) {
  <div class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fadeIn">
    <div
      class="bg-white dark:bg-surface-1 rounded-xl shadow-xl w-full max-w-md overflow-hidden card-bg border border-slate-200 dark:border-line">
      <div class="px-6 py-4 border-b border-slate-100 dark:border-line flex justify-between items-center">
        <h3 class="text-lg font-semibold text-slate-800 dark:text-white">
          {{
          currentLandingPageIndex !== null
          ? "Edit Landing Page"
          : "Add Landing Page"
          }}
        </h3>
        <button (click)="closeLandingPageModal()"
          class="text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300 transition-colors">
          <app-icon name="x" [size]="20"></app-icon>
        </button>
      </div>

      <form [formGroup]="landingPageForm" (ngSubmit)="saveLandingPage()" class="p-6 space-y-4">
        <app-input (keydown)="preventLeadingSpace($event)" variant="custom" placeholder="https://example.com/page"
          label="Landing Page URL" formControlName="url" [error]="getLandingPageError('url')"></app-input>

        <div class="grid grid-cols-2 gap-4">
          <app-dropdown [options]="landingPageMonths" title="Month" formControlName="month"
            [error]="getLandingPageError('month')" variant="themeBase" size="lg"></app-dropdown>
          <app-dropdown [options]="years" [maxYear]="currentYear" title="Year" formControlName="year"
            [error]="getLandingPageError('year')" variant="themeBase" size="lg"></app-dropdown>
        </div>

        <app-input (keydown)="preventDecimal($event)" variant="custom" type="number" placeholder="Enter Clicks"
          label="Clicks" formControlName="clicks"
          inputClass="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          [error]="getLandingPageError('clicks')"></app-input>

        <div class="flex justify-end gap-3 mt-6 pt-2">
          <app-button type="button" variant="defaultBorder" (buttonClick)="closeLandingPageModal()">Cancel</app-button>
          <app-button type="submit" variant="default">
            {{ currentLandingPageIndex !== null ? "Update" : "Add" }}
          </app-button>
        </div>
      </form>
    </div>
  </div>
  }

  <!-- City Modal -->
  @if (isCityModalOpen()) {
  <div class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fadeIn">
    <div
      class="bg-white dark:bg-surface-1 rounded-xl shadow-xl w-full max-w-md overflow-hidden card-bg border border-slate-200 dark:border-line">
      <div class="px-6 py-4 border-b border-slate-100 dark:border-line flex justify-between items-center">
        <h3 class="text-lg font-semibold text-slate-800 dark:text-white">
          {{ currentCityIndex !== null ? "Edit City" : "Add City" }}
        </h3>
        <button (click)="closeCityModal()"
          class="text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300 transition-colors">
          <app-icon name="x" [size]="20"></app-icon>
        </button>
      </div>

      <form [formGroup]="cityForm" (ngSubmit)="saveCity()" class="p-6 space-y-4">
        <app-input (keydown)="preventLeadingSpace($event)" variant="custom" placeholder="New York" label="City Name"
          formControlName="city" [error]="getCityError('city')"></app-input>

        <div class="grid grid-cols-2 gap-4">
          <app-dropdown [options]="cityMonths" title="Month" formControlName="month" [error]="getCityError('month')"
            variant="themeBase" size="lg"></app-dropdown>
          <app-dropdown [options]="years" [maxYear]="currentYear" title="Year" formControlName="year"
            [error]="getCityError('year')" variant="themeBase" size="lg"></app-dropdown>
        </div>

        <app-input (keydown)="preventDecimal($event)" variant="custom" type="number" placeholder="Enter User Count"
          label="User Count"
          inputClass="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          formControlName="newUsers" [error]="getCityError('newUsers')"></app-input>

        <div class="flex justify-end gap-3 mt-6 pt-2">
          <app-button type="button" variant="defaultBorder" (buttonClick)="closeCityModal()">Cancel</app-button>
          <app-button type="submit" variant="default">
            {{ currentCityIndex !== null ? "Update" : "Add" }}
          </app-button>
        </div>
      </form>
    </div>
  </div>
  }
  <!-- Review Modal -->
  @if (isReviewModalOpen()) {
  <div class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fadeIn">
    <div
      class="bg-white dark:bg-surface-1 rounded-xl shadow-xl w-full max-w-md overflow-hidden card-bg border border-slate-200 dark:border-line">
      <div class="px-6 py-4 border-b border-slate-100 dark:border-line flex justify-between items-center">
        <h3 class="text-lg font-semibold text-slate-800 dark:text-white">
          {{ currentReviewIndex !== null ? "Edit Review" : "Add Review" }}
        </h3>
        <button (click)="closeReviewModal()"
          class="text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300 transition-colors">
          <app-icon name="x" [size]="20"></app-icon>
        </button>
      </div>

      <form [formGroup]="reviewForm" (ngSubmit)="saveReview()" class="p-6 space-y-4">
        <app-dropdown [options]="platformOptions()" placeholder="Select Platform" label="Platform"
          formControlName="platform" [error]="getReviewError('platform')" variant="themeBase" size="lg">
        </app-dropdown>

        <div class="grid grid-cols-2 gap-4">
          <app-dropdown [options]="reviewMonths" title="Month" formControlName="month" [error]="getReviewError('month')"
            variant="themeBase" size="lg"></app-dropdown>
          <app-dropdown [options]="years" [maxYear]="currentYear" title="Year" formControlName="year"
            [error]="getReviewError('year')" variant="themeBase" size="lg"></app-dropdown>
        </div>

        <app-input variant="custom" type="number" placeholder="4.5" label="Rating (0-5)" formControlName="rating"
          inputClass="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          [error]="getReviewError('rating')"></app-input>

        <app-input (keydown)="preventDecimal($event)" variant="custom" type="number" placeholder="100"
          label="Reviews Count" formControlName="reviewsCount"
          inputClass="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          [error]="getReviewError('reviewsCount')"></app-input>

        <div class="flex justify-end gap-3 mt-6 pt-2">
          <app-button type="button" variant="defaultBorder" (buttonClick)="closeReviewModal()">Cancel</app-button>
          <app-button type="submit" variant="default">
            {{ currentReviewIndex !== null ? "Update" : "Add" }}
          </app-button>
        </div>
      </form>
    </div>
  </div>
  }
</ng-container>